/**
 * Desktop sidebar (>= 1024px). Replaces the bottom tab bar AND the "More"
 * screen on desktop: every destination is one click away. Phone layout keeps
 * BottomNav + ProfileMenu unchanged.
 */

import { Link, useLocation, useNavigate } from 'react-router';
import {
  Home, Dumbbell, CalendarDays, Apple, TrendingUp,
  Users, ClipboardList, CalendarClock,
  MessageCircle, Bot, Heart, Ruler, Coins, BookOpen, Quote, Settings, LogOut,
  type LucideIcon,
  Camera,
} from 'lucide-react';
import { isTrainerRole } from '@fitplatform/shared';
import { useAuth } from '@/lib/auth';
import { useBrand } from '@/lib/useBrand';
import { useAiCoachAvailable } from '@/lib/useAiCoachStatus';

interface NavItem {
  href: string;
  label: string;
  icon: LucideIcon;
  /** Small tag after the label (e.g. "Soon"). */
  tag?: string;
  /** Extra path prefixes that should also light this item up. */
  also?: string[];
  exact?: boolean;
}

const clientPrimary: NavItem[] = [
  { href: '/home', label: 'Home', icon: Home, exact: true, also: ['/home/appointments'] },
  { href: '/workouts', label: 'Workouts', icon: Dumbbell },
  { href: '/calendar', label: 'Calendar', icon: CalendarDays },
  { href: '/nutrition', label: 'Nutrition', icon: Apple },
  { href: '/home/progress', label: 'Progress', icon: TrendingUp },
];

const clientSecondary: NavItem[] = [
  { href: '/profile/messages', label: 'Messages', icon: MessageCircle },
  { href: '/profile/ai-coach', label: 'AI Coach', icon: Bot },
  { href: '/profile/health', label: 'Health Data', icon: Heart },
  { href: '/profile/measurements', label: 'Body Measurements', icon: Ruler },
  { href: '/profile/progress-photos', label: 'Progress Photos', icon: Camera },
  { href: '/profile/credits', label: 'Wallet', icon: Coins },
  { href: '/community', label: 'Journal', icon: BookOpen },
  { href: '/profile/testimonial', label: 'Leave a Testimonial', icon: Quote },
];

const coachPrimary: NavItem[] = [
  { href: '/coach/clients', label: 'Clients', icon: Users },
  { href: '/coach/programs', label: 'Programs', icon: ClipboardList },
  { href: '/coach/schedule', label: 'Schedule', icon: CalendarClock },
];

const coachSecondary: NavItem[] = [
  { href: '/profile/messages', label: 'Messages', icon: MessageCircle },
  { href: '/community', label: 'Journal', icon: BookOpen },
];

function isActive(pathname: string, item: NavItem): boolean {
  const match = (p: string) => (item.exact ? pathname === p : pathname === p || pathname.startsWith(`${p}/`));
  return match(item.href) || (item.also ?? []).some((p) => pathname.startsWith(p));
}

function NavLink({ item, pathname }: { item: NavItem; pathname: string }) {
  const active = isActive(pathname, item);
  const Icon = item.icon;
  return (
    <Link
      to={item.href}
      className={`side-nav-item ${active ? 'active' : ''}`}
      aria-current={active ? 'page' : undefined}
    >
      <Icon size={18} strokeWidth={active ? 2.2 : 1.7} />
      <span>{item.label}</span>
      {item.tag && (
        <span className="ml-auto text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full" style={{ border: '1px solid var(--gold-muted)', color: 'var(--gold-muted)' }}>
          {item.tag}
        </span>
      )}
    </Link>
  );
}

export function SideNav() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const brand = useBrand();
  const coach = isTrainerRole(user?.role);
  const primary = coach ? coachPrimary : clientPrimary;
  const aiAvailable = useAiCoachAvailable();
  const secondary = (coach ? coachSecondary : clientSecondary).map((i) =>
    i.href === '/profile/ai-coach' && aiAvailable === false ? { ...i, tag: 'Soon' } : i
  );

  const email = user?.email ?? '';
  const name =
    user?.display_name?.trim() ||
    `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim() ||
    (email.includes('@') ? email.split('@')[0] : email);

  async function handleLogout() {
    await logout();
    navigate('/login');
  }

  return (
    <aside className="side-nav" aria-label="Main navigation">
      {/* Logo (if uploaded) + brand name, styled like the admin app's header. */}
      <Link to={coach ? '/coach/clients' : '/home'} className="side-nav-brand" aria-label={brand.name}>
        {brand.logoUrl && (
          <img src={brand.logoUrl} alt="" style={{ width: 34, height: 34, objectFit: 'contain', flexShrink: 0 }} />
        )}
        <span>{brand.name}</span>
      </Link>

      <nav className="flex flex-col gap-0.5">
        {primary.map((item) => <NavLink key={item.href} item={item} pathname={pathname} />)}
      </nav>

      <p className="eyebrow side-nav-heading">{coach ? 'More' : 'You'}</p>
      <nav className="flex flex-col gap-0.5">
        {secondary.map((item) => <NavLink key={item.href} item={item} pathname={pathname} />)}
      </nav>

      <div className="side-nav-footer">
        <NavLink item={{ href: '/profile/settings', label: 'Settings', icon: Settings }} pathname={pathname} />
        <div className="flex items-center gap-3 px-3 pt-3 mt-2" style={{ borderTop: '1px solid rgba(244,239,227,0.08)' }}>
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 overflow-hidden"
            style={{ background: 'var(--gold)', color: 'var(--navy)' }}
          >
            {user?.avatar_url ? (
              <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
            ) : (
              (name[0] ?? '?').toUpperCase()
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate" style={{ color: 'var(--cream)' }}>{name}</p>
            <p className="text-[11px] truncate" style={{ color: 'var(--gray-500)' }}>{email}</p>
          </div>
          <button
            onClick={handleLogout}
            className="p-1.5 rounded-md transition-colors hover:bg-[var(--surface)]"
            style={{ color: 'var(--gray-400)' }}
            aria-label="Sign out"
            title="Sign out"
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </aside>
  );
}
